import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { CUSTOMER_PROFILE_STATUSES } from '@solar/contracts';

import {
  AdminIcon,
  ManagementHeader,
  MetricCard,
  adminManagementStyles as styles,
  formatAdminDate,
} from '../../../components/admin/AdminManagement';
import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { customersApi } from '../../../features/customers/customers.api';
import { operationalApi } from '../../../features/reporting/operational.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatStatusLabel } from '../../../lib/format-status';
import { routeTo } from '../../../routes/paths';

export function AdminCustomersPage(): React.JSX.Element {
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const customers = useQuery({
    queryKey: ['admin', 'customers', { page, search: appliedSearch, status }],
    queryFn: () =>
      customersApi.listAdmin({
        page,
        pageSize: 20,
        search: appliedSearch,
        status: status || undefined,
      }),
  });
  const summary = useQuery({
    queryKey: ['admin', 'customers', 'summary'],
    queryFn: () => operationalApi.customerSummary(),
  });
  const items = customers.data?.items ?? [];

  return (
    <div className={styles.page}>
      <ManagementHeader
        eyebrow="Customer operations"
        title="Customers"
        subtitle="Search customer profiles and monitor onboarding progress."
        icon="customer"
        context="Customer directory"
      />

      <section className={styles.metrics} aria-label="Customer summary">
        {summary.isError && <Alert tone="error">Customer summary is unavailable.</Alert>}
        {summary.data && (
          <>
            <MetricCard
              label="Total customers"
              value={summary.data.totalCustomers}
              icon="customer"
            />
            <MetricCard
              label="Onboarding complete"
              value={summary.data.onboardingComplete}
              icon="check"
              tone="blue"
            />
            <MetricCard
              label="Onboarding incomplete"
              value={summary.data.onboardingIncomplete}
              icon="clock"
              tone="amber"
            />
            <MetricCard
              label="Active subscriptions"
              value={summary.data.withActiveSubscription}
              icon="spark"
              tone="violet"
            />
          </>
        )}
      </section>

      <form
        className={styles.filterPanel}
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          setAppliedSearch(search.trim());
        }}
        aria-label="Filter customers"
      >
        <label>
          Search
          <input
            value={search}
            placeholder="Name, phone or customer number"
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label>
          Status
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
          >
            <option value="">All statuses</option>
            {CUSTOMER_PROFILE_STATUSES.map((value) => (
              <option value={value} key={value}>
                {formatStatusLabel(value)}
              </option>
            ))}
          </select>
        </label>
        <button type="submit">
          <AdminIcon name="search" /> Search
        </button>
      </form>

      <div className={styles.contentGrid}>
        <section className={styles.mainCard} aria-labelledby="customer-directory-heading">
          <header className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <span className={styles.sectionIcon}>
                <AdminIcon name="customer" />
              </span>
              <div>
                <h2 id="customer-directory-heading">Customer directory</h2>
                <p>Verified profile and onboarding data</p>
              </div>
            </div>
            {customers.data && (
              <span className={styles.countBadge}>
                {customers.data.pagination.totalItems} profiles
              </span>
            )}
          </header>
          {customers.isError && (
            <div className={styles.alertWrap}>
              <Alert tone="error">
                {toDisplayMessage(customers.error)}{' '}
                <Button variant="secondary" onClick={() => void customers.refetch()}>
                  Retry
                </Button>
              </Alert>
            </div>
          )}
          {customers.isPending && <p className={styles.empty}>Loading customers…</p>}
          {customers.isSuccess && items.length === 0 && (
            <p className={styles.empty}>No customers match the current filters.</p>
          )}
          {items.length > 0 && (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Customer number</th>
                    <th>Phone</th>
                    <th>Onboarding stage</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((customer) => (
                    <tr key={customer.id}>
                      <td>
                        <div className={styles.primaryCell}>
                          <span className={styles.avatar}>
                            {customer.firstName.slice(0, 1)}
                            {customer.lastName.slice(0, 1)}
                          </span>
                          <span>
                            <strong>
                              {customer.firstName} {customer.lastName}
                            </strong>
                            <small>Customer profile</small>
                          </span>
                        </div>
                      </td>
                      <td>
                        <Link to={routeTo.adminCustomer(customer.id)}>
                          {customer.customerNumber}
                        </Link>
                      </td>
                      <td>{customer.phoneNumber}</td>
                      <td>{formatStatusLabel(customer.onboardingStage)}</td>
                      <td>
                        <span
                          className={`${styles.status} ${customer.status === 'ACTIVE' ? styles.success : styles.warning}`}
                        >
                          {formatStatusLabel(customer.status)}
                        </span>
                      </td>
                      <td>{formatAdminDate(customer.createdAt)}</td>
                      <td>
                        <Link to={routeTo.adminCustomer(customer.id)}>View profile →</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {customers.data && customers.data.pagination.totalPages > 1 && (
            <nav className={styles.pagination}>
              <Button
                variant="secondary"
                disabled={!customers.data.pagination.hasPreviousPage}
                onClick={() => setPage((value) => value - 1)}
              >
                Previous
              </Button>
              <span>
                Page {customers.data.pagination.page} of {customers.data.pagination.totalPages}
              </span>
              <Button
                variant="secondary"
                disabled={!customers.data.pagination.hasNextPage}
                onClick={() => setPage((value) => value + 1)}
              >
                Next
              </Button>
            </nav>
          )}
        </section>

        <aside className={styles.rail}>
          <section className={styles.railCard}>
            <h2>Onboarding health</h2>
            <p>Backend-defined profile stages across the customer base.</p>
            <ul className={styles.railList}>
              {summary.data?.onboardingByStage.map((stage) => (
                <li key={stage.status}>
                  <span>
                    <i className={styles.dot} />
                    {formatStatusLabel(stage.status)}
                  </span>
                  <strong>{stage.count}</strong>
                </li>
              ))}
            </ul>
          </section>
          <section className={styles.railCard}>
            <h2>Recent registrations</h2>
            <p>New profiles from the operational summary.</p>
            <ul className={styles.railList}>
              {summary.data?.recentlyRegistered.slice(0, 5).map((customer) => (
                <li key={customer.customerId}>
                  <span>{customer.displayName}</span>
                  <Link to={customer.detailRoute}>Open</Link>
                </li>
              ))}
              {summary.data?.recentlyRegistered.length === 0 && <li>No recent registrations</li>}
            </ul>
          </section>
          <section className={styles.railCard}>
            <h2>Completed journeys</h2>
            <p>Customers with a completed subscription.</p>
            <ul className={styles.railList}>
              <li>
                <span>
                  <i className={styles.dot} />
                  Completed subscriptions
                </span>
                <strong>{summary.data?.withCompletedSubscription ?? '—'}</strong>
              </li>
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}
